import React, { useEffect, useState } from "react";
import { useApi } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { money, stageLabel, STAGE_COLORS, STAGES } from "@/lib/format";
import { toast } from "sonner";

const COLUMN_GROUPS = [
  { key: "new", label: "New", stages: ["new"] },
  { key: "qualified", label: "Qualified", stages: ["qualified", "research"] },
  { key: "outreach", label: "Outreach", stages: ["outreach"] },
  { key: "engaged", label: "Engaged", stages: ["engaged"] },
  { key: "agreement", label: "Agreement", stages: ["agreement_sent", "agreement_signed"] },
  { key: "filed", label: "Filed", stages: ["filed"] },
  { key: "closed", label: "Closed", stages: ["approved", "disbursed", "denied", "disqualified", "unresponsive"] },
];

export default function Pipeline() {
  const { get, patch } = useApi();
  const nav = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const r = await get("/contacts?limit=500");
    setContacts(r.data.items || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const grouped = (group) => contacts.filter(c => group.stages.includes(c.stage));
  const groupValue = (group) => grouped(group).reduce((s, c) => s + (c.surplus_amount || 0), 0);

  const onStageChange = async (id, newStage) => {
    try {
      await patch(`/contacts/${id}`, { stage: newStage });
      toast.success(`Moved to ${stageLabel(newStage)}`);
      setContacts((prev) => prev.map(c => c.id === id ? { ...c, stage: newStage } : c));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Workflow</p>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">
          Pipeline
        </h1>
        <p className="text-sm text-slate-600 mt-1">Every deal at a glance. Click a card to open the full profile.</p>
      </div>

      {loading ? (
        <div className="text-slate-500">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 cw-stagger">
          {COLUMN_GROUPS.map((g) => {
            const items = grouped(g);
            return (
              <div key={g.key} className="bg-slate-100 rounded-lg p-3 min-h-[400px]" data-testid={`pipeline-col-${g.key}`}>
                <div className="flex items-center justify-between mb-3 px-1">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-700">{g.label}</p>
                    <p className="text-[11px] text-slate-500 font-mono">{items.length} · {money(groupValue(g))}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {items.map((c) => {
                    const deadline = c.claim_deadline ? new Date(c.claim_deadline) : null;
                    const days = deadline ? Math.ceil((deadline - new Date()) / 86400000) : null;
                    return (
                      <Card
                        key={c.id}
                        data-testid={`pipeline-card-${c.id}`}
                        className="p-3 border-slate-200 bg-white hover:border-slate-400 hover:shadow-sm transition cursor-pointer"
                        onClick={() => nav(`/contacts/${c.id}`)}
                      >
                        <div className="font-semibold text-sm text-slate-900 truncate">{c.client_name || "—"}</div>
                        <div className="text-xs text-slate-500 truncate mt-0.5">{c.property_street}, {c.property_city}</div>
                        <div className="flex items-center justify-between mt-2">
                          <span className="font-mono text-sm font-bold text-emerald-700">{money(c.surplus_amount)}</span>
                          {days !== null && (
                            <Badge className={days < 30 ? "bg-red-100 text-red-800 border-red-200" :
                              days < 90 ? "bg-amber-100 text-amber-800 border-amber-200" :
                              "bg-slate-100 text-slate-700 border-slate-200"}>
                              {days}d
                            </Badge>
                          )}
                        </div>
                        <div className="mt-2 flex items-center justify-between gap-2">
                          <Badge variant="outline" className={`${STAGE_COLORS[c.stage] || ""} text-[10px]`}>
                            {stageLabel(c.stage)}
                          </Badge>
                          <div onClick={(e) => e.stopPropagation()}>
                            <Select value={c.stage} onValueChange={(v) => onStageChange(c.id, v)}>
                              <SelectTrigger className="h-6 text-[10px] w-24 px-2" data-testid={`pipeline-stage-select-${c.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {STAGES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                  {items.length === 0 && (
                    <div className="text-xs text-slate-400 text-center py-6">No deals</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
